import { devices } from 'shared/styles/components';
import styled from 'styled-components';

export const ProductContainer = styled.div`
  margin: auto;
  padding: 48px 24px;
  max-width: 840px;

  h2 {
    margin-top: 8px;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-family: ${({ theme }) => theme.fontFamily.display};
    font-size: 20px;
    line-height: 24px;
  }

  p {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 24px;
  }
`;

export const ProductDivider = styled.div`
  margin: 56px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ProductQuote = styled.div`
  margin: 32px 0;
  padding: 16px;
  border-left: 4px solid ${({ theme }) => theme.colors.bgSecondary};
  border-radius: 2px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 16px;
  line-height: 24px;
`;

export const ProductFeatureContainer = styled.div`
  position: relative;
  margin-right: auto;
  margin-left: auto;

  @media ${devices.mobileMax} {
    & > h2 {
      margin-bottom: 10px;
      font-size: 24px;
      line-height: 140%;
    }
  }
`;

export const ProductFeatureLayoutGrid = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto auto;
  grid-row-gap: 16px;
  grid-column-gap: 16px;
  margin: 0 auto;
  padding: 0px 8px;
  max-height: 1600px;
  overflow-y: hidden;
  transition: max-height 250ms ease-in-out;

  &.opened:last-of-type {
    margin-bottom: 16px;
    padding-bottom: 16px;
  }
  &:not(.opened):last-of-type {
    margin-bottom: 16px;
  }

  .name {
    max-width: 250px;
    font-size: 18px;
    line-height: 24px;
  }

  .details {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    gap: 12px;

    .status,
    .offering {
      display: flex;
      align-items: center;
      white-space: nowrap;

      .dot {
        margin-top: 1px;
        border-radius: 50%;
        width: 8px;
        height: 8px;
        margin-right: 6px;
        flex-shrink: 0;

        // Status
        &.available {
          background-color: ${({ theme }) => theme.colors.success};
        }
        &.in-development {
          background-color: ${({ theme }) => theme.colors.warning};
        }
        ,
        &.continuous-development {
          background-color: ${({ theme }) => theme.colors.info};
        }
        &.partially-available {
        }
        &.planned {
          background-color: ${({ theme }) => theme.colors.bgColor};
        }
        &.not-planned {
          background-color: ${({ theme }) => theme.colors.error};
        }
        // Offering type
        &.free {
          background-color: ${({ theme }) => theme.colors.bgPrimary};
        }
        &.plus {
          background-color: ${({ theme }) => theme.colors.update};
        }
      }
    }
  }

  .description {
    font-size: 14px;
    line-height: 20px;
  }

  &.featured {
    grid-template-columns: 1fr 1fr;
    margin: 60px auto 0;
    max-height: 800px;

    .name {
      max-width: 380px;
      font-size: 20px;
      line-height: 26px;
    }
    .description {
      font-size: 16px;
      line-height: 22px;
    }
  }

  &.hidden {
    max-height: 0;
  }

  @media ${devices.tabletLMax} {
    margin-top: 20px;
    grid-template-columns: 1fr 1fr;
    max-height: fit-content;

    &.featured {
      margin-top: 30px;
      grid-template-columns: 1fr;
      max-height: 1000px;
    }
  }

  @media ${devices.tabletMax} {
    grid-template-columns: 1fr;

    &.featured {
      grid-template-columns: 1fr;
    }
  }
`;

export const ProductFeatureLayoutGridNode = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-bottom: 0px;
  padding: 24px;
  max-width: none;
  height: 100%;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.bgSecondary};
  user-select: none;
  transition: all 200ms ease;

  grid-column-start: span 1;
  grid-column-end: span 1;
  grid-row-start: span 1;
  grid-row-end: span 1;

  grid-column-gap: 12px;
  grid-row-gap: 20px;

  &.isFeatured {
    padding: 40px;

    .content {
      grid-row-gap: 12px;
    }
  }

  &.has-link {
    cursor: pointer;
  }

  .image {
    margin-left: auto;
    margin-right: auto;
    max-width: 90%;
    max-height: 250px;

    img {
      max-width: 100%;
      border-radius: 16px;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    grid-row-gap: 8px;
    width: 100%;
  }

  .name {
    margin: 0px auto 4px;
    color: ${({ theme }) => theme.colors.textEmphasis};
    font-family: ${({ theme }) => theme.fontFamily.headline};
    font-weight: 500;
    text-align: center;
  }

  .description {
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }

  &:hover {
    box-shadow: 0 8px 15px 0 rgb(128 128 128 / 15%);
  }

  @media ${devices.tabletLMax} {
    padding: 24px;
  }
`;

export const ProductFeatureShowMoreContainer = styled.div`
  position: relative;
  display: flex;
  min-height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const ProductFeatureShowMoreLink = styled.div`
  padding: 14px 32px;
  color: ${({ theme }) => theme.colors.white};
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  border-radius: 28px;
  background-color: ${({ theme }) => theme.colors.bgColor};
  cursor: pointer;
  user-select: none;
  transition:
    box-shadow 300ms ease,
    transform 300ms ease,
    background-color 300ms ease,
    -webkit-transform 300ms ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.bgColorAlpha};
    box-shadow: 0 2px 9px 0 rgb(6 10 11 / 42%);
    transform: translate(0px, -2px);
  }
`;

export const ProductPlan = styled.div`
  padding: 0;
`;

export const ProductFeatureRequest = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 48px;

  h3 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    font-family: ${({ theme }) => theme.fontFamily.display};
    line-height: 28px;
    text-align: center;
  }

  a {
    margin-bottom: auto;
    padding: 14px 32px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    border-radius: 28px;
    color: ${({ theme }) => theme.colors.white};
    background-color: ${({ theme }) => theme.colors.bgColor};
    cursor: pointer;
    transition-property: background-color, box-shadow;
    transition-duration: 0.2s;

    &:hover {
      text-decoration: none;
      background-color: hsl(225, 39%, 56%);
      background-color: hsl(225, 39%, calc(var(--lightness-factor, 1) * 51%));
      box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
    }
  }
`;
